/**
 * RailTabs (DESIGN.md "Comments sit in parts"): the rail's head on a post page.
 * Comments with its count and Chat with M7, drawn as the chat rail's own head:
 * 56 tall, Manrope 600 13.5 with a 2px accent base under the open tab, and the
 * chat's 36px collapse icon button.
 * The client sees Comments only. One sentence of real state may sit under the
 * tabs; there is none when nothing true needs saying.
 */
import type { KeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { Sparkles } from "lucide-react";
import { KitTabIndicator } from "../../components/kit/TabIndicator";
import type { RailTab } from "../railCommentsStore";

export default function RailTabs({
  tab,
  onTab,
  count,
  subtitle,
  showChat = true,
  chatPanel = true,
  collapse,
  idPrefix = "co-rail",
  onPointerDown,
}: {
  tab: RailTab;
  onTab: (tab: RailTab) => void;
  count: number;
  subtitle?: string | null;
  /** The team's rail; the client sees Comments only. */
  showChat?: boolean;
  /**
   * Whether the chat is a panel beside Comments (the desk's rail). In the
   * tablet and phone sheets it is not: Chat with M7 is then a plain button
   * that hands over to the agent's own sheet, with no tab role.
   */
  chatPanel?: boolean;
  /** The collapse (or close) icon button on the right. */
  collapse?: ReactNode;
  /** Keeps tab and panel ids unique when a sheet and the rail coexist. */
  idPrefix?: string;
  /** A phone sheet drags from its head. */
  onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
}) {
  const tabs: RailTab[] = showChat && chatPanel ? ["comments", "chat"] : ["comments"];
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const index = tabs.indexOf(tab);
    const next = tabs[(index + (event.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length];
    if (!next || next === tab) return;
    event.preventDefault();
    onTab(next);
    requestAnimationFrame(() => document.getElementById(`${idPrefix}-tab-${next}`)?.focus());
  };

  return (
    <div className="co-railtabs" onPointerDown={onPointerDown}>
      <div className="co-railtabs-row">
        <div className="co-railtabs-list">
          <div role="tablist" aria-label="Rail" className="co-railtabs-tablist" data-indicator onKeyDown={onKeyDown}>
            <button
              id={`${idPrefix}-tab-comments`}
              type="button"
              role="tab"
              aria-selected={tab === "comments"}
              aria-controls={`${idPrefix}-panel-comments`}
              tabIndex={tab === "comments" ? 0 : -1}
              className="co-railtabs-tab"
              onClick={() => onTab("comments")}
            >
              {/* One run of text, so the count sits on the title's baseline. */}
              <span>
                Comments
                {count > 0 ? (
                  <span className="co-railtabs-count" aria-label={`${count} open`}>
                    {count}
                  </span>
                ) : null}
              </span>
            </button>
            {showChat && chatPanel ? (
              <button
                id={`${idPrefix}-tab-chat`}
                type="button"
                role="tab"
                aria-selected={tab === "chat"}
                aria-controls={`${idPrefix}-panel-chat`}
                tabIndex={tab === "chat" ? 0 : -1}
                className="co-railtabs-tab"
                onClick={() => onTab("chat")}
              >
                <Sparkles className="co-railtabs-spark" strokeWidth={1.8} aria-hidden="true" />
                Chat with M7
              </button>
            ) : null}
            <KitTabIndicator selectedKey={tab} />
          </div>
          {showChat && !chatPanel ? (
            <button type="button" className="co-railtabs-tab" onClick={() => onTab("chat")}>
              <Sparkles className="co-railtabs-spark" strokeWidth={1.8} aria-hidden="true" />
              Chat with M7
            </button>
          ) : null}
        </div>
        {collapse}
      </div>
      {subtitle && tab === "comments" ? <p className="co-railtabs-sub">{subtitle}</p> : null}
    </div>
  );
}
